export type IndicatorId =
  | 'volume'
  | 'rsi'
  | 'macd'
  | 'sma'
  | 'ema'
  | 'wma'
  | 'wema'
  | 'typicalPrice'
  | 'obv'
  | 'bollingerBands'
  | 'williamsR'
  | 'atr'
  | 'mfi'
  | 'stochastic'
  | 'roc'
  | 'trix'
  | 'vwap'
  | 'adl'
  | 'adx'
  | 'ao'
  | 'cci'
  | 'forceIndex'
  | 'kst'
  | 'psar'
  | 'stochRsi'
  | 'volumeProfile'
  | 'ichimokuCloud';

interface IndicatorMeta {
  labelKey: string;
  color: string;
  descriptionKey: string;
}

export type IndicatorPlacement = 'main' | 'pane';

export const INDICATOR_DEFAULT_PLACEMENT: Record<IndicatorId, IndicatorPlacement> = {
  volume: 'pane',
  rsi: 'pane',
  macd: 'pane',
  sma: 'main',
  ema: 'main',
  wma: 'main',
  wema: 'main',
  typicalPrice: 'main',
  obv: 'pane',
  bollingerBands: 'main',
  williamsR: 'pane',
  atr: 'pane',
  mfi: 'pane',
  stochastic: 'pane',
  roc: 'pane',
  trix: 'pane',
  vwap: 'main',
  adl: 'pane',
  adx: 'pane',
  ao: 'pane',
  cci: 'pane',
  forceIndex: 'pane',
  kst: 'pane',
  psar: 'main',
  stochRsi: 'pane',
  volumeProfile: 'main', // locked to main chart
  ichimokuCloud: 'main',
};

export const INDICATOR_METADATA: Record<IndicatorId, IndicatorMeta> = {
  volume: {
    labelKey: 'indicator.volume.label',
    color: 'blue',
    descriptionKey: 'indicator.volume.description',
  },
  rsi: {
    labelKey: 'indicator.rsi.label',
    color: 'purple',
    descriptionKey: 'indicator.rsi.description',
  },
  macd: {
    labelKey: 'indicator.macd.label',
    color: 'gold',
    descriptionKey: 'indicator.macd.description',
  },
  sma: {
    labelKey: 'indicator.sma.label',
    color: 'cyan',
    descriptionKey: 'indicator.sma.description',
  },
  ema: {
    labelKey: 'indicator.ema.label',
    color: 'lime',
    descriptionKey: 'indicator.ema.description',
  },
  wma: {
    labelKey: 'indicator.wma.label',
    color: 'geekblue',
    descriptionKey: 'indicator.wma.description',
  },
  wema: {
    labelKey: 'indicator.wema.label',
    color: 'volcano',
    descriptionKey: 'indicator.wema.description',
  },
  typicalPrice: {
    labelKey: 'indicator.typicalPrice.label',
    color: 'magenta',
    descriptionKey: 'indicator.typicalPrice.description',
  },
  obv: {
    labelKey: 'indicator.obv.label',
    color: 'orange',
    descriptionKey: 'indicator.obv.description',
  },
  bollingerBands: {
    labelKey: 'indicator.bollingerBands.label',
    color: 'geekblue',
    descriptionKey: 'indicator.bollingerBands.description',
  },
  williamsR: {
    labelKey: 'indicator.williamsR.label',
    color: 'gold',
    descriptionKey: 'indicator.williamsR.description',
  },
  atr: {
    labelKey: 'indicator.atr.label',
    color: 'blue',
    descriptionKey: 'indicator.atr.description',
  },
  mfi: {
    labelKey: 'indicator.mfi.label',
    color: 'green',
    descriptionKey: 'indicator.mfi.description',
  },
  stochastic: {
    labelKey: 'indicator.stochastic.label',
    color: 'purple',
    descriptionKey: 'indicator.stochastic.description',
  },
  roc: {
    labelKey: 'indicator.roc.label',
    color: 'cyan',
    descriptionKey: 'indicator.roc.description',
  },
  trix: {
    labelKey: 'indicator.trix.label',
    color: 'magenta',
    descriptionKey: 'indicator.trix.description',
  },
  vwap: {
    labelKey: 'indicator.vwap.label',
    color: 'gold',
    descriptionKey: 'indicator.vwap.description',
  },
  adl: {
    labelKey: 'indicator.adl.label',
    color: 'blue',
    descriptionKey: 'indicator.adl.description',
  },
  adx: {
    labelKey: 'indicator.adx.label',
    color: 'lime',
    descriptionKey: 'indicator.adx.description',
  },
  ao: {
    labelKey: 'indicator.ao.label',
    color: 'green',
    descriptionKey: 'indicator.ao.description',
  },
  cci: {
    labelKey: 'indicator.cci.label',
    color: 'purple',
    descriptionKey: 'indicator.cci.description',
  },
  forceIndex: {
    labelKey: 'indicator.forceIndex.label',
    color: 'orange',
    descriptionKey: 'indicator.forceIndex.description',
  },
  kst: {
    labelKey: 'indicator.kst.label',
    color: 'geekblue',
    descriptionKey: 'indicator.kst.description',
  },
  psar: {
    labelKey: 'indicator.psar.label',
    color: 'volcano',
    descriptionKey: 'indicator.psar.description',
  },
  stochRsi: {
    labelKey: 'indicator.stochRsi.label',
    color: 'purple',
    descriptionKey: 'indicator.stochRsi.description',
  },
  volumeProfile: {
    labelKey: 'indicator.volumeProfile.label',
    color: 'gold',
    descriptionKey: 'indicator.volumeProfile.description',
  },
  ichimokuCloud: {
    labelKey: 'indicator.ichimokuCloud.label',
    color: 'green',
    descriptionKey: 'indicator.ichimokuCloud.description',
  },
};

export const INDICATOR_LIST: IndicatorId[] = [
  'volume',
  'rsi',
  'macd',
  'sma',
  'ema',
  'wma',
  'wema',
  'typicalPrice',
  'obv',
  'bollingerBands',
  'williamsR',
  'atr',
  'mfi',
  'stochastic',
  'roc',
  'trix',
  'vwap',
  'adl',
  'adx',
  'ao',
  'cci',
  'forceIndex',
  'kst',
  'psar',
  'stochRsi',
  'volumeProfile',
  'ichimokuCloud',
];
